import { LAFAYETTE_UI_PART_PREFIX, parseLafayetteUiPart, } from "@lafayette/schemas";
// The client side of the agent stream (CHT-715; docs/architecture/phase-4-ui-protocol.md sections 5, 7 and 8). It reads the AI
// SDK UI message stream (SSE) that `POST /customer/agent/runs` returns and builds a typed result. It has no framework and no
// vendor dependency, so the mobile app and the web app both use it, and a test feeds it a saved fixture with no live model.
//
// It never throws on stream content. A part it cannot read becomes a generic `unsupported` item; a stream that ends before
// its terminal `run-status`, `finish` and `[DONE]` is `INTERRUPTED`. Every list and every text is bounded.
/** The header that marks the stream. The value `v1` is the AI SDK UI message stream protocol. */
export const AGENT_STREAM_HEADER = "x-vercel-ai-ui-message-stream";
export const AGENT_STREAM_LIMITS = Object.freeze({
    /** One SSE event, in UTF-8 bytes. A larger one is skipped, never parsed. */
    eventMaxBytes: 65_536,
    /** The text of one text part, in characters. More is cut (`truncated`). */
    textMaxChars: 65_536,
    /** The Lafayette parts of one answer that the state keeps (the server sends at most 8). */
    maxParts: 8,
    /** The unsupported-content items that the state keeps. More are only counted. */
    maxUnsupportedItems: 64,
    maxTextItems: 8,
});
export const initialAgentStreamState = Object.freeze({
    messageId: null,
    items: [],
    status: "STREAMING",
    runStatus: null,
    outcome: null,
    finishReason: null,
    done: false,
    integrity: "OK",
    skippedEvents: 0,
    droppedItems: 0,
});
/**
 * The UTF-8 length of a string in bytes, with no Node or Web API (`TextEncoder` and `Buffer` may be missing in a React Native
 * runtime). A surrogate pair is 4 bytes; a lone surrogate is 3 (the replacement character), as `TextEncoder` writes it.
 */
export function utf8Length(text) {
    let bytes = 0;
    for (let index = 0; index < text.length; index += 1) {
        const unit = text.charCodeAt(index);
        if (unit < 0x80)
            bytes += 1;
        else if (unit < 0x800)
            bytes += 2;
        else if (unit >= 0xd800 && unit <= 0xdbff && index + 1 < text.length) {
            const next = text.charCodeAt(index + 1);
            if (next >= 0xdc00 && next <= 0xdfff) {
                bytes += 4;
                index += 1;
            }
            else
                bytes += 3;
        }
        else
            bytes += 3;
    }
    return bytes;
}
/**
 * An incremental SSE parser. `feed` takes decoded text in any split and returns the complete events. It accepts LF, CR LF and
 * CR line ends, ignores comment lines (`: keep-alive`, `: stream-open`) and fields other than `data`, and joins the lines of
 * one event with a line feed. An event larger than `eventMaxBytes` is skipped (one `oversized` event), and the parser keeps
 * no more than that in memory.
 */
export function createSseParser(maxBytes = AGENT_STREAM_LIMITS.eventMaxBytes) {
    let buffer = "";
    let pendingCr = false;
    let dataLines = [];
    let dataBytes = 0;
    let skipping = false;
    const lineEnd = (events, line) => {
        if (line === "") {
            // A blank line ends the event.
            if (skipping)
                events.push({ kind: "oversized" });
            else if (dataLines.length > 0)
                events.push({ kind: "data", data: dataLines.join("\n") });
            dataLines = [];
            dataBytes = 0;
            skipping = false;
            return;
        }
        if (line.startsWith(":") || skipping)
            return;
        const separator = line.indexOf(":");
        const field = separator === -1 ? line : line.slice(0, separator);
        if (field !== "data")
            return;
        let value = separator === -1 ? "" : line.slice(separator + 1);
        if (value.startsWith(" "))
            value = value.slice(1);
        dataBytes += utf8Length(value) + 1;
        if (dataBytes > maxBytes) {
            skipping = true;
            dataLines = [];
            return;
        }
        dataLines.push(value);
    };
    return {
        feed(text) {
            const events = [];
            let input = (pendingCr ? "\r" : "") + text;
            pendingCr = false;
            // A trailing CR may be the first half of CR LF: keep it for the next chunk.
            if (input.endsWith("\r")) {
                input = input.slice(0, -1);
                pendingCr = true;
            }
            buffer += input;
            let start = 0;
            for (let index = 0; index < buffer.length; index += 1) {
                const char = buffer[index];
                if (char === "\n" || char === "\r") {
                    lineEnd(events, buffer.slice(start, index));
                    if (char === "\r" && buffer[index + 1] === "\n")
                        index += 1;
                    start = index + 1;
                }
            }
            buffer = buffer.slice(start);
            // A line with no end that is already past the cap: drop it now, so memory stays bounded.
            // (`length * 3` is the most bytes `length` units can be, so the exact count runs only when it could matter.)
            if (buffer.length > maxBytes ||
                (buffer.length * 3 > maxBytes && utf8Length(buffer) > maxBytes)) {
                skipping = true;
                dataLines = [];
                buffer = "";
            }
            return events;
        },
        /** The stream ended. A held CR was a line end, so it may complete an event. A half event is not an event. */
        end() {
            const events = pendingCr ? this.feed("\n") : [];
            pendingCr = false;
            buffer = "";
            dataLines = [];
            skipping = false;
            return events;
        },
    };
}
// --- the reducer -----------------------------------------------------------------------------------------------------
const RUN_STATUS_TYPE = `${LAFAYETTE_UI_PART_PREFIX}run-status`;
function withIntegrity(state) {
    const received = state.items.filter((item) => item.kind !== "text").length + state.droppedItems;
    const complete = state.runStatus !== null && state.finishReason !== null && state.done;
    return {
        ...state,
        // An abort is final: a later run-status does not make the stream complete.
        status: state.status === "INTERRUPTED" ? "INTERRUPTED" : complete ? "COMPLETE" : "STREAMING",
        integrity: state.runStatus !== null && state.runStatus.partCount > received
            ? "PARTS_MISSING"
            : state.integrity,
    };
}
function addItem(state, item) {
    // AI SDK reconciliation: a data part with an id that was already seen UPDATES that part, in place. It never adds a second
    // item and does not count against a cap. A part with no id is never merged.
    if (item.kind !== "text" && item.id !== "") {
        const known = state.items.findIndex((entry) => entry.kind !== "text" && entry.id === item.id);
        if (known !== -1)
            return { ...state, items: state.items.map((entry, at) => (at === known ? item : entry)) };
    }
    if (item.kind === "unsupported") {
        const count = state.items.filter((entry) => entry.kind === "unsupported").length;
        if (count >= AGENT_STREAM_LIMITS.maxUnsupportedItems) {
            return { ...state, droppedItems: state.droppedItems + 1 };
        }
    }
    else if (item.kind === "part") {
        const count = state.items.filter((entry) => entry.kind === "part").length;
        if (count >= AGENT_STREAM_LIMITS.maxParts) {
            return { ...state, droppedItems: state.droppedItems + 1 };
        }
    }
    return { ...state, items: [...state.items, item] };
}
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
/** Applies one SSE event to the state. Pure. Never throws. */
export function reduceAgentStream(state, event) {
    if (state.done)
        return state; // Nothing counts after [DONE].
    if (event.kind === "oversized")
        return { ...state, skippedEvents: state.skippedEvents + 1 };
    if (event.data === "[DONE]")
        return withIntegrity({ ...state, done: true });
    let chunk;
    try {
        chunk = JSON.parse(event.data);
    }
    catch {
        return { ...state, skippedEvents: state.skippedEvents + 1 };
    }
    if (!isRecord(chunk) || typeof chunk.type !== "string") {
        return { ...state, skippedEvents: state.skippedEvents + 1 };
    }
    const type = chunk.type;
    const id = typeof chunk.id === "string" ? chunk.id : "";
    switch (type) {
        case "start":
            return typeof chunk.messageId === "string" ? { ...state, messageId: chunk.messageId } : state;
        case "text-start": {
            // The same id again (a repeated or late `text-start`) neither opens a second item nor reopens an ended one.
            if (state.items.some((item) => item.kind === "text" && item.id === id))
                return state;
            if (state.items.filter((item) => item.kind === "text").length >=
                AGENT_STREAM_LIMITS.maxTextItems) {
                return { ...state, droppedItems: state.droppedItems + 1 };
            }
            return addItem(state, { kind: "text", id, text: "", done: false, truncated: false });
        }
        case "text-delta": {
            if (typeof chunk.delta !== "string")
                return { ...state, skippedEvents: state.skippedEvents + 1 };
            const delta = chunk.delta;
            return {
                ...state,
                items: state.items.map((item) => {
                    if (item.kind !== "text" || item.id !== id || item.done)
                        return item;
                    const room = AGENT_STREAM_LIMITS.textMaxChars - item.text.length;
                    return room >= delta.length
                        ? { ...item, text: item.text + delta }
                        : { ...item, text: item.text + delta.slice(0, Math.max(0, room)), truncated: true };
                }),
            };
        }
        case "text-end":
            return {
                ...state,
                items: state.items.map((item) => item.kind === "text" && item.id === id ? { ...item, done: true } : item),
            };
        case "finish":
            return withIntegrity({
                ...state,
                finishReason: typeof chunk.finishReason === "string" ? chunk.finishReason : "unknown",
            });
        case "abort":
            return withIntegrity({ ...state, status: "INTERRUPTED" });
        case "error":
            // The server sends no `error` chunk (a degraded run ends with run-status). Show nothing of its text.
            return withIntegrity({ ...state, skippedEvents: state.skippedEvents + 1 });
        default:
            break;
    }
    if (type.startsWith(LAFAYETTE_UI_PART_PREFIX)) {
        const result = parseLafayetteUiPart(type, chunk.data);
        if (type === RUN_STATUS_TYPE) {
            if (result.status === "ok" && result.part.name === "run-status") {
                return withIntegrity({
                    ...state,
                    runStatus: result.part.data,
                    outcome: result.part.data.outcome,
                });
            }
            // An unreadable terminal part: the stream has no terminal state, so it stays incomplete.
            return withIntegrity({ ...state, skippedEvents: state.skippedEvents + 1 });
        }
        if (result.status === "ok")
            return withIntegrity(addItem(state, { kind: "part", id, part: result.part }));
        return withIntegrity(addItem(state, {
            kind: "unsupported",
            id,
            reason: result.reason,
            name: result.name,
        }));
    }
    // Any other chunk (`start-step`, a tool chunk, a `data-` part of another prefix): the app does not use it.
    return state;
}
// --- reading a stream ------------------------------------------------------------------------------------------------
/** True when the response is the agent UI message stream (an event stream with the AI SDK marker). */
export function isAgentStreamResponse(headers) {
    const contentType = headers.get("content-type")?.toLowerCase() ?? "";
    return contentType.startsWith("text/event-stream") && headers.get(AGENT_STREAM_HEADER) === "v1";
}
/**
 * Reads a whole response body into a result. `onState` is called after every event that changed the state (a UI may render
 * the text as it grows). The returned state is `COMPLETE` only after the terminal `run-status`, `finish` and `[DONE]`; any
 * other end (a cut connection, a reader error, an abort) is `INTERRUPTED`. Never rejects.
 */
export async function readAgentStream(body, onState) {
    const decoder = new TextDecoder("utf-8");
    const parser = createSseParser();
    let state = initialAgentStreamState;
    const reader = body.getReader();
    try {
        for (;;) {
            const next = await reader.read();
            if (next.done)
                break;
            for (const event of parser.feed(decoder.decode(next.value, { stream: true }))) {
                const reduced = reduceAgentStream(state, event);
                if (reduced !== state) {
                    state = reduced;
                    onState?.(state);
                }
            }
            if (state.done)
                break;
        }
        for (const event of parser.end())
            state = reduceAgentStream(state, event);
    }
    catch {
        // A reader error is an interrupted stream.
    }
    finally {
        void reader.cancel().catch(() => undefined);
    }
    const final = state.status === "COMPLETE" ? state : { ...state, status: "INTERRUPTED" };
    if (final !== state)
        onState?.(final);
    return final;
}
/** The same, over text that is already in memory (a saved fixture, a test). */
export function readAgentStreamText(text) {
    const parser = createSseParser();
    let state = initialAgentStreamState;
    for (const event of parser.feed(text))
        state = reduceAgentStream(state, event);
    for (const event of parser.end())
        state = reduceAgentStream(state, event);
    return state.status === "COMPLETE" ? state : { ...state, status: "INTERRUPTED" };
}
